export type LicenseRedeemResult =
  | {
      ok: true;
      licenseKey: string;
      unlockToken: string;
      activatedAt: string;
    }
  | {
      ok: false;
      error: LicenseRedeemError;
    };

export type LicenseRedeemError = "invalid_key" | "already_redeemed" | "disabled" | "server_error";

export const licenseApiUrl = import.meta.env.VITE_LICENSE_API_URL || "/license/redeem";
export const accessLandingUrl = import.meta.env.VITE_ACCESS_LANDING_URL || "https://bubgt.com/lp-tlc-icg";
export const localTestLicenseKey = "TLC-LOCAL-TEST";

export function normalizeLicenseKey(value: string): string {
  return value.trim().toUpperCase().replace(/\s+/g, "");
}

export function licenseErrorMessage(error: LicenseRedeemError): string {
  if (error === "already_redeemed") return "License key ini sudah pernah dipakai di device lain.";
  if (error === "disabled") return "License key ini sedang nonaktif. Hubungi admin untuk bantuan.";
  if (error === "server_error") return "Server validasi sedang bermasalah. Coba lagi sebentar lagi.";
  return "License key tidak valid. Pastikan kamu menyalin key dengan benar.";
}

export async function redeemLicenseKey(input: {
  licenseKey: string;
  deviceId: string;
  apiUrl?: string;
}): Promise<LicenseRedeemResult> {
  const licenseKey = normalizeLicenseKey(input.licenseKey);
  if (isLocalTestLicense(licenseKey)) {
    return {
      ok: true,
      licenseKey,
      unlockToken: `local-test-${input.deviceId}`,
      activatedAt: new Date().toISOString()
    };
  }

  const response = await fetch(input.apiUrl ?? licenseApiUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ licenseKey, deviceId: input.deviceId })
  }).catch(() => undefined);

  if (!response) return { ok: false, error: "server_error" };

  const body = (await response.json().catch(() => undefined)) as Partial<LicenseRedeemResult> | undefined;
  if (!body) return { ok: false, error: "server_error" };
  if (response.ok && body?.ok === true && body.unlockToken && body.activatedAt) {
    return {
      ok: true,
      licenseKey,
      unlockToken: body.unlockToken,
      activatedAt: body.activatedAt
    };
  }

  const error = body.ok === false ? body.error : "server_error";
  return { ok: false, error: normalizeRedeemError(error) };
}

function normalizeRedeemError(error: unknown): LicenseRedeemError {
  if (error === "already_redeemed" || error === "disabled" || error === "server_error") return error;
  return "invalid_key";
}

function isLocalTestLicense(licenseKey: string): boolean {
  if (licenseKey !== localTestLicenseKey) return false;
  if (typeof window === "undefined") return false;
  return window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
}
